/**
 * DataChart Component
 * High-performance time series chart using uPlot
 */

import React, { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "uplot/dist/uPlot.min.css";

import { DataChartProps } from './types';
import { DEFAULT_COLOR, DEFAULT_POINT_SIZE, CHART_HEIGHT, LOCALE, POINT_VISIBILITY_THRESHOLD } from './constants';

// Defined here to avoid circular dependency issues with re-exports
const DEFAULT_Y_DOMAIN: [number | "auto", number | "auto"] = ["auto", "auto"];
import { formatDisplayTime, countVisiblePoints } from './utils';
import {
  useFindFirstNumericKey,
  useResolvedValueKey,
  useProcessedData,
  useChartInstance
} from './hooks';

const DataChart: React.FC<DataChartProps> = ({
  data,
  valueKey,
  unit,
  yDomain = DEFAULT_Y_DOMAIN,
  color = DEFAULT_COLOR,
  pointSize = DEFAULT_POINT_SIZE,
  showSeriesInfo = true,
}) => {
  const { t } = useTranslation('charts');
  const containerRef = useRef<HTMLDivElement>(null);
  const showPointsRef = useRef(false);
  const [showPointsEnabled, setShowPointsEnabled] = useState(true);
  const showPointsEnabledRef = useRef(true);

  // Data processing hooks
  const findFirstNumericKey = useFindFirstNumericKey();
  const resolvedValueKey = useResolvedValueKey(data, valueKey, findFirstNumericKey);
  const processedData = useProcessedData(data, resolvedValueKey);

  // Chart instance hook
  const { chartRef, handleResetZoom } = useChartInstance({
    containerRef,
    processedData,
    resolvedValueKey,
    unit,
    yDomain,
    color,
    pointSize,
    showPointsRef,
    showPointsEnabledRef
  });

  // Sync showPointsEnabled state with ref and trigger redraw
  useEffect(() => {
    showPointsEnabledRef.current = showPointsEnabled;
    if (chartRef.current) {
      const u = chartRef.current;
      const timestamps = u.data[0] as number[];
      if (timestamps && timestamps.length > 0) {
        const xMin = u.scales.x.min ?? timestamps[0];
        const xMax = u.scales.x.max ?? timestamps[timestamps.length - 1];
        const visibleCount = countVisiblePoints(timestamps, xMin, xMax);
        showPointsRef.current = visibleCount <= POINT_VISIBILITY_THRESHOLD && showPointsEnabled;
        // redraw(false, false) = don't reset scales, don't batch
        u.redraw(false, false);
      }
    }
  }, [showPointsEnabled]);

  // Early return if no valid value key found
  if (!resolvedValueKey) {
    return (
      <div className="w-full h-96 flex items-center justify-center text-muted-foreground">
        <p>{t("dataChart.noValidColumns", "No valid numeric columns found")}</p>
      </div>
    );
  }

  if (processedData.validCount === 0) {
    return (
      <div className="w-full h-96 flex items-center justify-center text-muted-foreground">
        <p>{t("dataChart.noValidData", "No valid data points found")}</p>
      </div>
    );
  }

  const valueLabel = unit ? `${resolvedValueKey} (${unit})` : resolvedValueKey;
  const displayStart = processedData.timestamps[0];
  const displayEnd = processedData.timestamps[processedData.timestamps.length - 1];

  return (
    <div className="w-full space-y-2 sm:space-y-4">
      {/* Header + Controls */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center justify-between gap-2 sm:gap-3">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {showSeriesInfo && (
            <>
              <span className="text-xs sm:text-sm font-medium text-foreground">
                {t("dataChart.series")}: <span className="font-semibold">{valueLabel}</span>
              </span>
              <span className="text-xs sm:text-sm text-muted-foreground">
                {t("dataChart.points")}:{" "}
                <span className="font-medium">
                  {processedData.validCount.toLocaleString(LOCALE)}
                </span>
              </span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <label className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-muted-foreground cursor-pointer">
            <span>{t("dataChart.showPoints", "Punkte anzeigen")}</span>
            <button
              type="button"
              role="switch"
              aria-checked={showPointsEnabled}
              onClick={() => setShowPointsEnabled(!showPointsEnabled)}
              className={`relative inline-flex h-4 w-7 sm:h-5 sm:w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${
                showPointsEnabled ? "bg-primary" : "bg-muted"
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-3 w-3 sm:h-4 sm:w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  showPointsEnabled ? "translate-x-3 sm:translate-x-4" : "translate-x-0"
                }`}
              />
            </button>
          </label>
          <button
            className="px-2 py-0.5 sm:px-3 sm:py-1 text-xs sm:text-sm rounded-md border border-border hover:bg-muted transition-colors"
            onClick={handleResetZoom}
            aria-label={t("dataChart.aria.resetZoom")}
          >
            {t("dataChart.resetZoom", "Reset Zoom")}
          </button>
        </div>
      </div>

      {/* Time range */}
      <div className="text-center text-xs sm:text-sm text-muted-foreground">
        {t("dataChart.timeRange")}: {formatDisplayTime(displayStart)} – {formatDisplayTime(displayEnd)}
      </div>

      {/* Zoom instructions */}
      <div className="text-[10px] sm:text-xs text-muted-foreground text-center">
        {t("dataChart.zoomHint")}
      </div>

      {/* Chart */}
      <style>{`
        .u-select {
          background: rgba(25, 118, 210, 0.15) !important;
          border: 1px dashed rgba(25, 118, 210, 0.5) !important;
        }
        .u-cursor-x, .u-cursor-y {
          border-color: rgba(25, 118, 210, 0.4) !important;
        }
        .u-legend {
          position: relative !important;
          margin-top: 8px !important;
          font-size: 10px !important;
        }
        @media (min-width: 640px) {
          .u-legend {
            font-size: 12px !important;
          }
        }
      `}</style>
      {/*
        uPlot is pinned to height={CHART_HEIGHT} and appends its legend (.u-legend)
        below the canvas inside this container. Using a fixed border-box `height`
        plus `pb-*` shrank the content box, so the canvas + legend overflowed and
        the legend ended up flush against the card border. `minHeight` reserves the
        space (no layout shift on mount) while letting the container grow to fit the
        legend; the bottom padding then lifts the legend clear of the card border.
        (Safe now that height is min-, not fixed: the container grows past the
        canvas + legend + padding instead of clipping them.)
      */}
      <div
        ref={containerRef}
        className="w-full pb-6 sm:pb-8"
        style={{ minHeight: CHART_HEIGHT }}
      />
    </div>
  );
};

export default React.memo(DataChart);
